import { useQuery } from '@tanstack/react-query';
import type { ClientArea } from '../types';
import { actions, registerActions } from '@/lib/actions';
import { goTo, navigate } from '@/lib/router';
import { registerCommands, registerNavDot, registerView, type PaletteCommand } from '@/lib/slots';
import { plural } from '@/lib/format';
import { toast, toastError } from '@/lib/toast';
import { setupCompose } from './compose';
import { fetchOutbox } from './compose/data';
import { countsQuery, latestListQuery, latestReplyQuery } from './data';

function useInboxDot(): string | null {
  const { data } = useQuery(countsQuery);
  return data && data.unreadMine > 0 ? 'New replies' : null;
}

function useMailCommands(): PaletteCommand[] {
  const reply = useQuery(latestReplyQuery).data;
  const list = useQuery(latestListQuery).data;
  const commands: PaletteCommand[] = [];
  if (reply) {
    commands.push({
      id: 'mail.reply-latest',
      title: 'Reply to the latest reply',
      sub: `${reply.name} · ${reply.subject}`,
      icon: 'reply',
      keywords: 'answer respond inbox',
      order: 110,
      run: () => {
        navigate({ view: 'inbox', sub: [reply.threadId], params: { f: null, q: null } });
        actions.compose({ mode: 'reply', threadId: reply.threadId });
      },
    });
  }
  commands.push({
    id: 'mail.list-send',
    title: 'Send to a list',
    sub: list ? `${list.name ?? 'Your last list'} · ${plural(list.personIds.length, 'person', 'people')}` : 'Pick people in People, then send them personal copies',
    icon: 'send',
    keywords: 'bulk mass newsletter personal copies',
    order: 120,
    run: () => {
      if (list) {
        actions.listSend({ personIds: list.personIds, listName: list.name ?? undefined });
        return;
      }
      goTo('people');
      toast('Select the people to write to, then choose Email', { icon: 'users' });
    },
  });
  return commands;
}

export const area: ClientArea = {
  name: 'mail',
  setup() {
    registerView('inbox', () => import('./InboxView'));
    registerNavDot('inbox', useInboxDot);
    registerCommands(useMailCommands);
    registerActions({
      openThread: (threadId: string) => navigate({ view: 'inbox', sub: [threadId], params: { f: null, q: null } }),
      openOutbox: (outboxId: string) => {
        fetchOutbox(outboxId).then(
          (o) => {
            if (o.status === 'sent' && o.sentThreadId) {
              navigate({ view: 'inbox', sub: [o.sentThreadId], params: { f: null, q: null } });
            } else if (o.status === 'sent') {
              toast(o.error ?? 'That email went out', { icon: 'send' });
            } else if (o.status === 'cancelled') {
              navigate({ view: 'inbox', sub: [], params: { f: 'scheduled', q: null } });
              toast('That email was cancelled', { icon: 'x' });
            } else {
              navigate({ view: 'inbox', sub: [o.id], params: { f: 'scheduled', q: null } });
            }
          },
          (err: unknown) => toastError(err),
        );
      },
    });
    setupCompose();
  },
};
